import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0102 from "../lesson-02/lesson";
import lesson0126 from "../lesson-26/lesson";

const linkLesson0102 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0102.id);
const linkLesson0126 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0126.id);

const theory = ` <h3 class=sectionHead>Porous material as a medium</h3>
<p>
   An acoustical absorbing material is a mechanical porous structure saturated by a viscous fluid. In this medium interaction between fluid and solid leads to an important acoustic dissipation due to viscosity and heat exchanges (see <a href="${linkLesson0126}" target="_blank">Lesson 1.26</a>). Fluid and structure interaction may also lead to structure vibration, sometime called skeleton vibrations. These skeleton vibrations are highly dependent on the mechanical boundary conditions and therefore are often neglected. So, porous material models have been developed mainly considering uniform and homogeneous materials. In that case the material can be seen as a medium in which waves propagate. This medium can be characterized by its characteristic impedance and wave number. For acoustics in a perfect gas, the characteristic impedance is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mi mathvariant=italic>γK</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the mean density and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>γ</mi>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>atm</mi>
         </mrow>
      </msub>
   </math>
   the incompressibility with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>γ</mi>
   </math>
   the ratio of specific heats and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>atm</mi>
         </mrow>
      </msub>
   </math>
   the ambient pressure (see <a href="${linkLesson0102}" target="_blank">Lesson 1.2</a>). The wave number is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jω</mi>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi>K</mi>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>. For porous materials it is common to describe the acoustic properties by using the concepts of equivalent density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and equivalent incompressibility or Bulk modulus
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   both quantities being complex. Be aware that in the literature there is commonly a confusion between compressibility and incompressibility, incompressibility being designated as the compressibility. Many models have been developed. We present here a largely used and rather general model.
<h3 class=sectionHead>Johnson Champoux Allard Lafarge model for a perfectly rigid material</h3>
<p>The basics of this model is to consider the fluid propagation as the one of fluid in tiny tortuous tubes of variable cross section. This leads to 6 parameters:
<ol class=enumerate1>
   <li class=enumerate id=x1-2002x1>
      Open porosity
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>ϕ</mi>
      </math>: this is the ratio of fluid volume to total volume. This is usually close to unity. The word ”open” is to specify that fluid in close pores is not considered.
   <li class=enumerate id=x1-2004x2>
      Tortuosity
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>α</mi>
            </mrow>
            <mrow>
               <mi>∞</mi>
            </mrow>
         </msub>
      </math>: this can be seen as the ratio of the travel length of the of the wave inside the material to the thickness of the material. Mathematically this is given by
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>α</mi>
            </mrow>
            <mrow>
               <mi>∞</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mover class=mml-overline accent=false>
                  <mrow>
                     <msup>
                        <mrow>
                           <mi>v</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                        </mrow>
                     </msup>
                  </mrow>
                  <mo accent=true>¯</mo>
               </mover>
            </mrow>
            <mrow>
              <msup>
                <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mover class=mml-overline accent=false>
                  <mrow>
                      <mi mathvariant="bold">v</mi>
                  </mrow>
                  <mo accent=true>¯</mo>
                  </mover>
                  <mo class=MathClass-rel>|</mo>
                </mrow>
                <mrow>
                    <mn>2</mn>
                </mrow>
              </msup>

            </mrow>
         </mfrac>
      </math>
      where
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>v</mi>
      </math>
      is the particle velocity and bars stand for the mean value on a given volume. This is usually comprised between 1 and 3.
   <li class=enumerate id=x1-2006x3>
      Viscous characteristic length
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>Λ</mi>
      </math>: this expresses the importance of the viscous effects. This is the ratio of the mean square velocity on the volume to the mean square velocity on the material surface. It lies in the range [10 350]
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>μ</mi>
      </math>m.
   <li class=enumerate id=x1-2008x4>
      Thermal characteristic length
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
               <mi>Λ</mi>
            </mrow>
            <mrow>
               <mi>′</mi>
            </mrow>
         </msup>
      </math>: this expresses the importance of the thermal effects. This is equivalent to the viscous characteristic length but for thermal effects and is usually one to three times larger.
   <li class=enumerate id=x1-2010x5>
      Static air flow resistivity
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>σ</mi>
      </math>: it characterizes, partly, the visco-inertial effects at low frequencies. Its range is rather large as it can be usually observed in the range
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo class=MathClass-open>[</mo>
         <msup>
            <mrow>
               <mn>10</mn>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </msup>
      </math>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
              <mn>10</mn>
            </mrow>
            <mrow>
              <mn>6</mn>
            </mrow>
         </msup>
         <mo class=MathClass-close>]</mo>
      </math> N.s.m
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
               <mn>4</mn>
            </mrow>
         </msup></math>.
   <li class=enumerate id=x1-2012x6>
      Static thermal permeability
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msubsup>
            <mrow>
               <mi>k</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mi>′</mi>
            </mrow>
         </msubsup>
      </math>: this is a surface which describes the thermal effects at low frequencies. It lies in the range [
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
               <mn>10</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
               <mn>10</mn>
            </mrow>
         </msup>
      </math>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
               <mn>10</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
               <mn>8</mn>
            </mrow>
         </msup>
      </math>] N<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>.
</ol>
<p>On the basis of these six parameters and those of air characteristics, equivalent density and equivalent incompressibility are given by
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy="false">(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>[</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                     </mrow>
                  </mfrac>
                  <msqrt>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>jω</mi>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>ω</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>∞</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                  </msqrt>
               </mrow>
               <mo form=postfix fence=true>]</mo>
            </mrow>
            </math>
            <td class=eq-no>(1)
            <tr>
            <td style="font-size: 11px">
            <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy="false" >(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>γ</mi>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>atm</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mrow>
                     <mo form=prefix fence=true>[</mo>
                     <mrow>
                        <mi>γ</mi>
                        <mo class=MathClass-bin>−</mo>
                        <mfrac>
                           <mrow>
                              <mi>γ</mi>
                              <mo class=MathClass-bin>−</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                              <mo class=MathClass-bin>+</mo>
                              <mfrac>
                                 <mrow>
                                    <msubsup>
                                       <mrow>
                                          <mi>ω</mi>
                                       </mrow>
                                       <mrow>
                                          <mn>0</mn>
                                       </mrow>
                                       <mrow>
                                          <mi>′</mi>
                                       </mrow>
                                    </msubsup>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                    <mi mathvariant=italic>jω</mi>
                                 </mrow>
                              </mfrac>
                              <msqrt>
                                 <mrow>
                                    <mn>1</mn>
                                    <mo class=MathClass-bin>+</mo>
                                    <mfrac>
                                       <mrow>
                                          <mi mathvariant=italic>jω</mi>
                                          <msubsup>
                                             <mrow>
                                                <mi>ω</mi>
                                             </mrow>
                                             <mrow>
                                                <mi>∞</mi>
                                             </mrow>
                                             <mrow>
                                                <mi>′</mi>
                                             </mrow>
                                          </msubsup>
                                       </mrow>
                                       <mrow>
                                          <msup>
                                             <mrow>
                                                <msubsup>
                                                   <mrow>
                                                      <mi>ω</mi>
                                                   </mrow>
                                                   <mrow>
                                                      <mn>0</mn>
                                                   </mrow>
                                                   <mrow>
                                                      <mi>′</mi>
                                                   </mrow>
                                                </msubsup>
                                             </mrow>
                                             <mrow>
                                                <mn>2</mn>
                                             </mrow>
                                          </msup>
                                       </mrow>
                                    </mfrac>
                                 </mrow>
                              </msqrt>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo form=postfix fence=true>]</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(2)
</table>
<p>where
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>σ</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            </math>
            <td class=eq-no>(3)
            <tr>
            <td>
            <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>∞</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>σ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>Λ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>μ</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>α</mi>
                           </mrow>
                           <mrow>
                              <mi>∞</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            </math>
            <td class=eq-no>(4)
            <tr>
            <td>
            <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msubsup>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <mi>ϕ</mi>
                  <msup>
                     <mrow>
                        <mi>ν</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            </math>
            <td class=eq-no>(5)
            <tr>
            <td>
            <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msubsup>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>16</mn>
                  <msup>
                     <mrow>
                        <mi>ν</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>Λ</mi>
                     </mrow>
                     <mrow>
                     <mi>′</mi>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
   </math>
   is the air viscosity and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>ν</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>μ</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi mathvariant=italic>Pr</mi>
         </mrow>
      </mfrac>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Pr</mi>
   </math>
   the air Prandtl number, ratio of viscous effects to thermal effects. For low frequencies it appears that
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy="false">(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>σ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωϕ</mi>
               </mrow>
            </mfrac>
            </math>
            <td class=eq-no>(7)
            <tr>
            <td style="font-size: 14px">
            <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy="false">(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>atm</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mrow>
                     <mo form=prefix fence=true>[</mo>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>−</mo>
                        <mfrac>
                           <mrow>
                              <mi>γ</mi>
                              <mo class=MathClass-bin>−</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>γ</mi>
                           </mrow>
                        </mfrac>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>jω</mi>
                           </mrow>
                           <mrow>
                              <mi>ϕ</mi>
                              <msup>
                                 <mrow>
                                    <mi>ν</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msup>
                              <msubsup>
                                 <mrow>
                                    <mi>k</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msubsup>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo form=postfix fence=true>]</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   So at low frequency real part of the compressibility allows to access to the porosity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>, its imaginary part allows to access to permeability
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   while the real part of the density allows to access to the tortuosity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
   </math>
   and its imaginary part to the resistivity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>σ</mi>
   </math>. For high frequencies it appears that
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy="false">(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Λ</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>μ</mi>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>

            </math>
            <td class=eq-no>(9)
            <tr>
            <td style="font-size: 13px">
            <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy="false">(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>γ</mi>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>atm</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>[</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>2</mn>
                        <mo class=MathClass-open>(</mo>
                        <mi>γ</mi>
                        <mo class=MathClass-bin>−</mo>
                        <mn>1</mn>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>Λ</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <msqrt>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mi>ν</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msup>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>jω</mi>
                           </mrow>
                        </mfrac>
                     </mrow>
                  </msqrt>
               </mrow>
               <mo form=postfix fence=true>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(10)
</table>
<p>So, at high frequency equivalent density and bulk modulus allow to access to the viscous and thermal characteristics lengths respectively.
<h3 class=sectionHead>Model including skeleton vibrations (Biot model)</h3>
<p>
   When the skeleton is not rigid the previous model needs to be completed in order to take into account the fluid structure interactions. In that case the material is seen as a diphasic medium with a fluid phase and a solid phase, this one being characterized, in the case of a uniform isotropic material, by its density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, Young’s modulus
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, Poisson’s coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ν</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, and loss factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>η</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>.
<h3 class=sectionHead>Impedance and absorption coefficient</h3>
<p>
   Equivalent density and incompressibility being known, the equivalent characteristic impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ceq</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
   </math>
   and wave number
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jω</mi>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>eq</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>K</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>eq</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   can be calculated from which the surface impedance can be deduced when the material is placed on a a rigid wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mat</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ceq</mi>
         </mrow>
      </msub>
      <mi class=qopname>tan</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
      <mi>e</mi>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>e</mi>
   </math>
   is the material thickness. Reflection coefficient at normal incidence can also be deduced:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mat</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mat</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. From this reflection coefficient, the absorption coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>R</mi>
      <msup>
         <mrow>
            <mo class=MathClass-rel>|</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   is derived. This coefficient is positive and lower than unity. In practice, it is near zero for low frequencies and near unity for high frequencies, transition depending on the thickness of the material and material properties. High frequency properties are obtained when the thickness is larger than a quarter wave length. In practice the efficiency is poor for low frequencies and actual research focus on material with double porosity (which means two sizes of porosity) or inclusions in order to increase efficiency in the low frequency range. An example of absorbing material absorption coefficient is given in <a href="#" data-img-src=${config.ASSETS_URL}/01/27/theory/figure-1.svg data-img-alt="Figure 1. Absorption coefficient of a material. Black curve : without skeleton vibrations, red curve : with skeleton vibrations. Thickness: 20 mm, porosity: 95 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>%</mi></math>, resistivity: 20 000 N.s.m<sup>-4</sup>, tortuosity: 1.00, viscous characteristic length: 80 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>μ</mi></math>m, thermal characteristic length: 200 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>μ</mi></math>m, static thermal permeability: 10<sup>-9</sup>m<sup>2</sup>, mass density: 30 kg m<sup>-3</sup>, Young’s modulus: 100 000 Pa, Poisson’s ratio: 0.4, loss factor: 0.05.">Figure 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/27/theory/figure-1.svg alt="Figure 1. Absorption coefficient of a material. Black curve : without skeleton vibrations, red curve : with skeleton vibrations. Thickness: 20 mm, porosity: 95 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>%</mi></math>, resistivity: 20 000 N.s.m<sup>-4</sup>, tortuosity: 1.00, viscous characteristic length: 80 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>μ</mi></math>m, thermal characteristic length: 200 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>μ</mi></math>m, static thermal permeability: 10<sup>-9</sup>m<sup>2</sup>, mass density: 30 kg m<sup>-3</sup>, Young’s modulus: 100 000 Pa, Poisson’s ratio: 0.4, loss factor: 0.05."><figcaption class=caption>Figure 1. Absorption coefficient of a material. Black curve : without skeleton vibrations, red curve : with skeleton vibrations. Thickness: 20 mm, porosity: 95 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>%</mi></math>, resistivity: 20 000 N.s.m<sup>-4</sup>, tortuosity: 1.00, viscous characteristic length: 80 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>μ</mi></math>m, thermal characteristic length: 200 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>μ</mi></math>m, static thermal permeability: 10<sup>-9</sup>m<sup>2</sup>, mass density: 30 kg.m<sup>-3</sup>, Young’s modulus: 100 000 Pa, Poisson’s ratio: 0.4, loss factor: 0.05.</figcaption></figure>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>J.-F. Allard and N. Atalla. Propagation of Sound in Porous Media—Modeling Sound Absorbing Materials. Wiley, 2009
</dl>`;

export default theory;
